import { answerFeedback, type TaughtAnswer } from "./yufa-answer-feedback.js";
import type { GradedAnswer } from "./answer-marks.js";
import {
  type ArrangementOrders,
  arrangementOrders,
} from "./arrangement-orders.js";
import { gradeArrangement } from "./grade-arrangement.js";
import { gradeHanzi } from "./grade-hanzi.js";
import { gradeRewrite } from "./grade-rewrite.js";
import { ratings } from "./rating.js";

/**
 * Marking one item of a grammar exercise 语法练习, with the graders a 复习
 * card is marked by.
 *
 * The graders are pure and run on both sides, which is what `docs/lianxi.md`
 * leans on: the public page marks in the browser through this, and the
 * `/user/lianxi/` handler marks a reported answer on the server through the
 * same two functions (`src/user/yufa-report.ts`). It lives under `src/fuxi/`
 * beside the graders for that reason: the browser bundle may not import from
 * `src/user/`, and the handler imports nothing from `src/site/`. A
 * gap is a `liju_cloze_to_hanzi` card's question and is marked as one, the
 * word in the other writing earning 还行 as it does there. An arrangement
 * passes only in the original order or a checked alternative. Review keeps
 * its conservative scheduling rating for other complete orders. The two
 * written kinds have no card. A Choose item is the sentence picked against
 * the right one, and a Transform item the rewrite typed against the list a
 * person wrote.
 *
 * An unrecognised complete order is unverified. The exercise offers another
 * attempt without making a claim about whether that order is grammatical.
 */

/** What a reader typed into a gap, and what the gap wanted. */
export interface GapAnswer {
  readonly kind: "gap";
  readonly typed: string;

  /** The word, as the article writes it. */
  readonly word: string;

  /** The same word in the other writing, or the empty string. */
  readonly other: string;
}

/** The order a reader put a sentence's pieces in, and the sentence's own. */
export interface ArrangeAnswer {
  readonly kind: "arrange";

  /** The pieces, in the sentence's own order. */
  readonly pieces: readonly string[];

  /** The pieces, in the order the reader put them. */
  readonly arranged: readonly string[];

  /** Additional checked orders, as places in `pieces`. */
  readonly accepted?: ArrangementOrders;
}

/** Which sentence a reader chose, and which was right, by their places. */
export interface ChooseAnswer extends TaughtAnswer {
  readonly options?: readonly string[];
  readonly kind: "choose";
  readonly chosen: number;
  readonly right: number;
}

/** A sentence a reader rewrote, and the rewrites that are right. */
export interface TransformAnswer extends TaughtAnswer {
  readonly kind: "transform";
  readonly typed: string;

  /** Every right rewrite, in the article's writing. */
  readonly accepted: readonly string[];

  /** The same rewrites in the other writing. */
  readonly other: readonly string[];
}

/** A repair passes when both the reviewed location and the rewrite match. */
export interface RepairAnswer extends TaughtAnswer {
  readonly kind: "repair";
  readonly chosen: number;
  readonly expected: number;
  readonly typed: string;
  readonly accepted: readonly string[];
  readonly other: readonly string[];
}

/** A selected form or insertion boundary, with completed sentences for reviewed feedback. */
interface FocusedAnswerBase extends TaughtAnswer {
  readonly chosen: number;
  readonly choices: readonly {
    readonly value: number;
    readonly text: string;
    readonly language?: "zh" | "en";
  }[];
  readonly accepted: readonly number[];
}
export type FocusedAnswer = FocusedAnswerBase &
  (
    | { readonly kind: "contrast" }
    | { readonly kind: "placement" }
    | { readonly kind: "interpretation" }
    | { readonly kind: "reply" }
  );

/** One item's answer. */
export type ItemAnswer =
  | GapAnswer
  | ArrangeAnswer
  | ChooseAnswer
  | TransformAnswer
  | RepairAnswer
  | FocusedAnswer;

/** An exercise's completion decision, separate from a Review rating. */
export interface ItemMark extends GradedAnswer {
  readonly outcome: "passed" | "incorrect" | "unverified";
}

/** Turn a card grade into an exercise result. */
function marked(graded: GradedAnswer, unverified = false): ItemMark {
  return {
    ...graded,
    outcome: unverified
      ? "unverified"
      : graded.rating === ratings.again
        ? "incorrect"
        : "passed",
  };
}

/**
 * Mark one answer.
 *
 * A Choose item is right or not, and nothing between. A Transform item is
 * marked against its list of rewrites by `./grade-rewrite.ts`.
 * @param answer - What the reader produced.
 * @returns The rating, and the marks a card would show.
 */
export function markItem(answer: ItemAnswer): ItemMark {
  if (answer.kind === "repair") {
    const graded = gradeRewrite(answer);
    const known = answerFeedback(answer);
    const unverified = known === undefined || known.status === "unverified";
    return marked(
      {
        ...graded,
        rating:
          answer.chosen === answer.expected && known?.status === "target"
            ? graded.rating
            : ratings.again,
      },
      unverified,
    );
  }
  if (
    answer.kind === "contrast" ||
    answer.kind === "placement" ||
    answer.kind === "interpretation" ||
    answer.kind === "reply"
  ) {
    const known = answerFeedback(answer);
    const selected =
      Number.isInteger(answer.chosen) &&
      answer.choices.some((choice) => choice.value === answer.chosen);
    return marked(
      {
        rating:
          selected &&
          answer.accepted.includes(answer.chosen) &&
          known?.status === "target"
            ? ratings.good
            : ratings.again,
        pieces: [],
        accepted: [],
      },
      selected && (known === undefined || known.status === "unverified"),
    );
  }
  if (answer.kind === "choose") {
    return marked({
      rating: answer.chosen === answer.right ? ratings.good : ratings.again,
      pieces: [],
      accepted: [],
    });
  }

  if (answer.kind === "transform") {
    const graded = gradeRewrite(answer);
    const known = answerFeedback(answer);
    return marked(
      graded,
      graded.rating === ratings.again &&
        (known === undefined || known.status === "unverified"),
    );
  }

  if (answer.kind === "gap") {
    const other = answer.other !== "" && answer.other !== answer.word;

    return marked(
      gradeHanzi({
        expected: answer.word,
        typed: answer.typed,
        ...(other ? { otherWritings: [answer.other] } : {}),
      }),
    );
  }

  const [first, ...rest] = answer.pieces;

  if (first === undefined) {
    return marked({ rating: ratings.again, pieces: [], accepted: [] });
  }

  const orders = arrangementOrders(answer.accepted, answer.pieces.length) ?? [];
  const graded = gradeArrangement({
    pieces: answer.pieces,
    arranged: answer.arranged,
    accepted: [
      [first, ...rest],
      ...orders.map((order) => order.map((place) => answer.pieces[place]!)),
    ],
  });

  return marked(graded, graded.rating === ratings.hard);
}

/** Whether a marked answer passes the item. */
export function passes(graded: ItemMark): boolean {
  return graded.outcome === "passed";
}

/** Whether a marked answer was right in another form, which earns 还行. */
export function nearlyRight(graded: ItemMark): boolean {
  return passes(graded) && graded.rating === ratings.hard;
}
